/**
 * LandingPage — public marketing page shown to unauthenticated visitors
 * before the sign-in screen. Promotes the product intent, the AWS
 * certification catalog, pricing, and contact/social info.
 *
 * Why this exists (two goals):
 *   1. Marketing — a modern, sleek front door for Dream&Build Exams.
 *   2. Legitimacy — a young domain that jumps straight to an AWS-branded
 *      login form trips phishing classifiers (Safe Browsing, SmartScreen,
 *      ISP filters like AT&T ActiveArmor). Leading with real business
 *      content — who we are, what we sell, pricing, contact, socials, and
 *      a clear "not affiliated with AWS" disclaimer — signals a legitimate
 *      education business instead of a credential-harvesting page.
 *
 * Design language intentionally mirrors the sibling app
 * (skills-platform / dreamandbuildcloudai.com): same #0e0c1a background,
 * #f05a28 orange accent, 1280px containers, and section rhythm
 * (topnav → hero with stat pills → trust strip → catalog → features →
 * pricing → FAQ → contact/footer).
 *
 * Props:
 *   onGetStarted: () => void   — hand off to the auth screen (sign up)
 *   onSignIn:     () => void   — hand off to the auth screen (sign in)
 *   certifications: Array      — AWS_CERTIFICATIONS from App.js
 */
import React, { useState } from 'react';
import './LandingPage.css';
import { formatBuildDate } from './buildTime';

const CONTACT_EMAIL = 'jossai@dreamandbuildllc.com';
const LINKEDIN_URL = 'https://www.linkedin.com/company/dream-build-cloud/?viewAsMember=true';
const FACEBOOK_URL = 'https://www.facebook.com/profile.php?id=61590375287237';
const PLATFORM_URL = 'https://dreamandbuildcloudai.com/?utm_source=exam-app&utm_campaign=landing';

const LEVEL_ORDER = ['Foundational', 'Associate', 'Professional', 'Specialty'];

const FEATURES = [
  {
    icon: '📝',
    title: 'Practice quizzes with instant feedback',
    desc: 'Work through exam-style questions at your own pace. Every question is graded immediately so you learn as you go, not just at the end.',
  },
  {
    icon: '⏱️',
    title: 'Realistic timed exam simulator',
    desc: 'Full-length, timed mock exams that mirror the real question count and time limit for each certification — so exam day feels familiar.',
  },
  {
    icon: '💡',
    title: 'A detailed explanation for every answer',
    desc: 'Each question includes why the correct answer is right and why the others are wrong, with links to the relevant AWS documentation.',
  },
  {
    icon: '🗳️',
    title: 'Community answer voting',
    desc: 'See how other learners voted on tricky questions and read their reasoning. Community input is reviewed before it is published.',
  },
  {
    icon: '🔁',
    title: 'Review mode after every attempt',
    desc: 'When you finish a quiz or exam, page back through every question with your answer, the correct answer, and the full explanation.',
  },
  {
    icon: '📊',
    title: 'Progress that follows you',
    desc: 'Your quiz history and scores are saved to your account, so you can pick up where you left off — even if a session times out.',
  },
];

const FAQ = [
  {
    q: 'What do I get with Dream&Build Exams?',
    a: 'Practice quizzes and full-length timed exam simulations across AWS certifications from Foundational to Specialty, with a detailed explanation for every question. Cloud Practitioner (CLF-C02) practice is free.',
  },
  {
    q: 'How does pricing work?',
    a: 'Cloud Practitioner (CLF-C02) is free. Any other single certification is $14.99/month and unlocks unlimited quizzes and timed exams for that cert. You can cancel anytime from your billing portal — access continues to the end of the period you paid for.',
  },
  {
    q: 'Do these questions come from the real AWS exam?',
    a: 'No. These are independent, exam-style practice questions written to help you learn the concepts each certification tests. They are not real exam questions and are for study purposes only.',
  },
  {
    q: 'Is this affiliated with Amazon Web Services?',
    a: 'No. Dream&Build Exams is an independent study platform. It is not affiliated with, endorsed by, or sponsored by Amazon Web Services. AWS, Amazon Web Services, and all certification names and logos are trademarks of Amazon.com, Inc. or its affiliates.',
  },
  {
    q: 'What if I want labs, capstones, and live sessions too?',
    a: 'That is the full Dream&Build Cloud Learning Platform — hands-on AWS labs, auto-graded capstone projects, a jobs board, AI-led practice interviews, and these same quizzes and exams in one subscription. There is a link in the nav and footer.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. Cancel from your billing portal at any time. Your access continues until the end of the current billing period — you are never cut off mid-cycle, and there are no long-term contracts.',
  },
];

function LandingPage({ onGetStarted, onSignIn, certifications = [] }) {
  const [openFaq, setOpenFaq] = useState(null);

  const available = certifications.filter((c) => c.available);
  const certCount = available.length;

  const byLevel = LEVEL_ORDER.map((level) => ({
    level,
    certs: available.filter((c) => c.level === level),
  })).filter((group) => group.certs.length > 0);

  return (
    <div className="lp-page">
      {/* ── Top nav ───────────────────────────────────────────── */}
      <header className="lp-topnav">
        <div className="lp-brand" aria-label="Dream&Build Exams home">
          <img src="/logo.png" alt="Dream&amp;Build" className="lp-brand-logo" />
        </div>
        <nav className="lp-topnav-links" aria-label="Marketing navigation">
          <a href="#certifications" className="lp-topnav-link">Certifications</a>
          <a href="#features" className="lp-topnav-link">Features</a>
          <a href="#pricing" className="lp-topnav-link">Pricing</a>
          <a href="#faq" className="lp-topnav-link">FAQ</a>
          <a href="#contact" className="lp-topnav-link">Contact</a>
        </nav>
        <div className="lp-topnav-ctas">
          <button type="button" className="lp-link-btn" onClick={onSignIn}>Sign in</button>
          <button type="button" className="lp-primary-btn" onClick={onGetStarted}>Get started</button>
        </div>
      </header>

      {/* ── Hero ──────────────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-hero-content">
          <div className="lp-hero-left">
            <p className="lp-eyebrow">
              AWS certification exam prep
              <span className="lp-updated-chip">Updated {formatBuildDate('short')}</span>
            </p>
            <h1 className="lp-hero-heading">
              Pass your AWS exam with{' '}
              <span className="lp-accent">realistic practice</span>.
            </h1>
            <p className="lp-hero-sub">
              Timed exam simulations and unlimited practice quizzes across every
              AWS certification path — each question backed by a clear explanation
              of why the answer is right and the others are wrong.
            </p>
            <div className="lp-hero-ctas">
              <button type="button" className="lp-cta-lg" onClick={onGetStarted}>
                Start free with Cloud Practitioner →
              </button>
              <a href="#pricing" className="lp-cta-lg-secondary">See pricing</a>
            </div>
            <div className="lp-hero-stats">
              <div className="lp-stat-pill">
                <span className="lp-stat-value">{certCount}</span>
                <span className="lp-stat-label">AWS certs covered</span>
              </div>
              <div className="lp-stat-pill">
                <span className="lp-stat-value">100%</span>
                <span className="lp-stat-label">Answers explained</span>
              </div>
              <div className="lp-stat-pill">
                <span className="lp-stat-value">$0</span>
                <span className="lp-stat-label">To start (CLF-C02)</span>
              </div>
            </div>
          </div>
          <div className="lp-hero-right" aria-hidden="true">
            <div className="lp-hero-orb" />
            <div className="lp-hero-card">
              <div className="lp-hero-card-top">
                <span className="lp-hero-card-tag">Sample question</span>
                <span className="lp-hero-card-timer">⏱️ 1:42</span>
              </div>
              <p className="lp-hero-card-q">
                A company needs to add a secondary CIDR to a VPC using
                172.16.0.0/16. Which block can it attach?
              </p>
              <ul className="lp-hero-card-opts">
                <li>10.0.0.0/16</li>
                <li className="lp-hero-card-correct">172.17.0.0/16 ✓</li>
                <li>192.168.0.0/16</li>
              </ul>
              <p className="lp-hero-card-exp">
                Same 172.16.0.0/12 range, /16 size, no overlap — the others
                are restricted RFC 1918 ranges.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Trust strip ───────────────────────────────────────── */}
      <section className="lp-trust" aria-label="Why Dream&Build Exams">
        <div className="lp-trust-grid">
          <article className="lp-trust-card">
            <span className="lp-trust-icon" aria-hidden="true">🎯</span>
            <h3 className="lp-trust-title">Built to mirror the real exam</h3>
            <p className="lp-trust-desc">
              Matching question counts and time limits per certification, so the
              simulator prepares you for the format, not just the content.
            </p>
          </article>
          <article className="lp-trust-card">
            <span className="lp-trust-icon" aria-hidden="true">💡</span>
            <h3 className="lp-trust-title">Learn from every answer</h3>
            <p className="lp-trust-desc">
              Detailed explanations and per-option rationales turn each question
              into a lesson — with references to AWS documentation.
            </p>
          </article>
          <article className="lp-trust-card">
            <span className="lp-trust-icon" aria-hidden="true">💳</span>
            <h3 className="lp-trust-title">Simple, honest pricing</h3>
            <p className="lp-trust-desc">
              Start free on Cloud Practitioner. Unlock any other cert for
              $14.99/month. Cancel anytime — no contracts.
            </p>
          </article>
        </div>
      </section>

      {/* ── Certification catalog ─────────────────────────────── */}
      <section id="certifications" className="lp-section lp-catalog">
        <div className="lp-section-inner">
          <h2 className="lp-section-heading">Every AWS certification path, one place.</h2>
          <p className="lp-section-sub">
            From your first Cloud Practitioner attempt to Professional and
            Specialty exams — practice quizzes and timed simulations for each.
          </p>
          {byLevel.map((group) => (
            <div key={group.level} className="lp-catalog-group">
              <h3 className="lp-catalog-level">{group.level}</h3>
              <div className="lp-catalog-cards">
                {group.certs.map((cert) => (
                  <article key={cert.id} className="lp-cert-card">
                    <span className="lp-cert-code">{cert.code}</span>
                    <h4 className="lp-cert-name">{cert.name}</h4>
                    <div className="lp-cert-meta">
                      <span>📄 {cert.examQuestions} questions</span>
                      <span>⏱️ {cert.examMinutes} min exam</span>
                    </div>
                    {cert.note && <p className="lp-cert-note">{cert.note}</p>}
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ──────────────────────────────────────────── */}
      <section id="features" className="lp-section lp-features">
        <div className="lp-section-inner">
          <h2 className="lp-section-heading">Everything you need to walk in ready.</h2>
          <div className="lp-feature-grid">
            {FEATURES.map((f) => (
              <article key={f.title} className="lp-feature-card">
                <span className="lp-feature-icon" aria-hidden="true">{f.icon}</span>
                <h3 className="lp-feature-title">{f.title}</h3>
                <p className="lp-feature-desc">{f.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ───────────────────────────────────────────── */}
      <section id="pricing" className="lp-section lp-pricing">
        <div className="lp-section-inner">
          <h2 className="lp-section-heading">Simple pricing that fits how you study.</h2>
          <p className="lp-section-sub">
            No bundles you don't need. Start free, then pay only for the cert
            you're working on.
          </p>
          <div className="lp-pricing-grid">
            <article className="lp-price-card">
              <div className="lp-price-name">Free</div>
              <div className="lp-price-tagline">Get a feel for it</div>
              <div className="lp-price-amount">$0<span className="lp-price-cadence"> forever</span></div>
              <ul className="lp-price-features">
                <li className="lp-yes">Cloud Practitioner (CLF-C02) practice</li>
                <li className="lp-yes">Practice quizzes with explanations</li>
                <li className="lp-yes">Answer review after each attempt</li>
                <li className="lp-no">Other certifications</li>
                <li className="lp-no">Timed exam simulator on paid certs</li>
              </ul>
              <button type="button" className="lp-price-cta" onClick={onGetStarted}>
                Start free
              </button>
            </article>

            <article className="lp-price-card lp-price-highlight">
              <div className="lp-price-badge">Most popular</div>
              <div className="lp-price-name">Single Certification</div>
              <div className="lp-price-tagline">Focus on one exam at a time</div>
              <div className="lp-price-amount">$14.99<span className="lp-price-cadence"> / month</span></div>
              <ul className="lp-price-features">
                <li className="lp-yes">Unlock any one certification</li>
                <li className="lp-yes">Unlimited practice quizzes</li>
                <li className="lp-yes">Full-length timed exam simulator</li>
                <li className="lp-yes">Detailed explanation for every question</li>
                <li className="lp-yes">Progress tracking &amp; review mode</li>
                <li className="lp-yes">Cancel anytime</li>
              </ul>
              <button type="button" className="lp-price-cta lp-price-cta-primary" onClick={onGetStarted}>
                Get started
              </button>
              <p className="lp-price-note">Switch which cert you're studying anytime.</p>
            </article>

            <article className="lp-price-card">
              <div className="lp-price-name">Full Learning Platform</div>
              <div className="lp-price-tagline">Labs, capstones &amp; more</div>
              <div className="lp-price-amount">Explore<span className="lp-price-cadence"> plans</span></div>
              <ul className="lp-price-features">
                <li className="lp-yes">Everything in the exam app</li>
                <li className="lp-yes">Hands-on AWS labs (we pay the bill)</li>
                <li className="lp-yes">Auto-graded capstone projects</li>
                <li className="lp-yes">Daily AWS jobs board</li>
                <li className="lp-yes">AI-led practice interviews</li>
              </ul>
              <a
                href={PLATFORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="lp-price-cta"
              >
                See platform plans →
              </a>
              <p className="lp-price-note">Separate subscription on dreamandbuildcloudai.com.</p>
            </article>
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────── */}
      <section id="faq" className="lp-section lp-faq">
        <div className="lp-section-inner lp-faq-inner">
          <h2 className="lp-section-heading">Questions, answered.</h2>
          <div className="lp-faq-list">
            {FAQ.map((item, i) => (
              <div key={i} className={`lp-faq-item ${openFaq === i ? 'open' : ''}`}>
                <button
                  type="button"
                  className="lp-faq-q"
                  aria-expanded={openFaq === i}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span>{item.q}</span>
                  <span className="lp-faq-chev" aria-hidden="true">{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && <p className="lp-faq-a">{item.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────── */}
      <section className="lp-final-cta">
        <h2 className="lp-final-heading">Start studying in under a minute.</h2>
        <p className="lp-final-sub">
          Free account. Full Cloud Practitioner practice. No credit card to start.
        </p>
        <button type="button" className="lp-cta-lg" onClick={onGetStarted}>
          Create your free account →
        </button>
      </section>

      {/* ── Contact / Footer ──────────────────────────────────── */}
      <footer id="contact" className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <img src="/logo.png" alt="Dream&amp;Build" className="lp-footer-logo" />
            <p className="lp-footer-tagline">
              An independent AWS certification practice platform by Dream&amp;Build LLC.
            </p>
          </div>

          <div className="lp-footer-col">
            <h4 className="lp-footer-col-title">Contact</h4>
            <a href={`mailto:${CONTACT_EMAIL}`} className="lp-footer-link">{CONTACT_EMAIL}</a>
            <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="lp-footer-link">LinkedIn</a>
            <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="lp-footer-link">Facebook</a>
          </div>

          <div className="lp-footer-col">
            <h4 className="lp-footer-col-title">Product</h4>
            <a href="#certifications" className="lp-footer-link">Certifications</a>
            <a href="#pricing" className="lp-footer-link">Pricing</a>
            <button type="button" className="lp-footer-link lp-footer-linkbtn" onClick={onSignIn}>Sign in</button>
            <a href={PLATFORM_URL} target="_blank" rel="noopener noreferrer" className="lp-footer-link">Learning Platform</a>
          </div>
        </div>

        <div className="lp-footer-legal">
          <p>
            <strong>Not affiliated with Amazon Web Services.</strong> Dream&amp;Build
            Exams is an independent study platform for educational purposes only.
            AWS, Amazon Web Services, and all related certification names and logos
            are trademarks of Amazon.com, Inc. or its affiliates. This platform is
            not endorsed by, sponsored by, or associated with AWS in any way.
            Practice questions are intended to aid learning and do not represent
            actual exam content.
          </p>
          <p className="lp-footer-copy">
            © {new Date().getFullYear()} Dream&amp;Build LLC. All rights reserved.
            {' · '}Last updated {formatBuildDate('medium')}
          </p>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
